import { useCallback, useMemo, useState, type Dispatch, type RefObject, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import type { CopilotChatState, CopilotProposal } from 'app/modules/wallforming/components/copilot/types/copilotTypes';

export const useCopilotConversationManager = (
    isSending: boolean,
    applyingRef: RefObject<boolean>,
    setPrompt: Dispatch<SetStateAction<string>>,
    setError: Dispatch<SetStateAction<string | null>>
) => {
    const { t } = useTranslation();
    const [chatState, setChatState] = useState<CopilotChatState>(() => {
        const id = uuidv4();
        return {
            conversations: [{ id, title: t('copilotNewChat'), messages: [] }],
            activeConversationId: id
        };
    });

    const activeConversation =
        chatState.conversations.find((conversation) => conversation.id === chatState.activeConversationId) ??
        chatState.conversations[0];
    const messages = useMemo(() => activeConversation?.messages ?? [], [activeConversation]);

    const createConversation = useCallback(() => {
        if (applyingRef.current) {
            return;
        }
        const id = uuidv4();
        setChatState((current) => ({
            conversations: [...current.conversations, { id, title: t('copilotNewChat'), messages: [] }],
            activeConversationId: id
        }));
        setPrompt('');
        setError(null);
    }, [applyingRef, setError, setPrompt, t]);

    const selectConversation = useCallback(
        (id: string) => {
            if (isSending || applyingRef.current) {
                return;
            }
            setChatState((current) => ({ ...current, activeConversationId: id }));
            setPrompt('');
            setError(null);
        },
        [applyingRef, isSending, setError, setPrompt]
    );

    const deleteConversation = useCallback(
        (id: string) => {
            if (applyingRef.current) {
                return;
            }
            setChatState((current) => {
                const conversations = current.conversations.filter((conversation) => conversation.id !== id);
                if (conversations.length === 0) {
                    const replacementId = uuidv4();
                    return {
                        conversations: [{ id: replacementId, title: t('copilotNewChat'), messages: [] }],
                        activeConversationId: replacementId
                    };
                }

                return {
                    conversations,
                    activeConversationId:
                        current.activeConversationId === id ? conversations[0].id : current.activeConversationId
                };
            });
            setPrompt('');
            setError(null);
        },
        [applyingRef, setError, setPrompt, t]
    );

    const renameConversation = useCallback(
        (id: string, title: string) => {
            if (applyingRef.current) {
                return;
            }
            setChatState((current) => ({
                ...current,
                conversations: current.conversations.map((conversation) =>
                    conversation.id === id ? { ...conversation, title } : conversation
                )
            }));
        },
        [applyingRef]
    );

    const updateProposal = useCallback((proposalId: string, update: Partial<CopilotProposal>) => {
        setChatState((current) => ({
            ...current,
            conversations: current.conversations.map((conversation) =>
                conversation.id !== current.activeConversationId ||
                !conversation.messages.some((message) =>
                    message.proposals?.some((proposal) => proposal.id === proposalId)
                )
                    ? conversation
                    : {
                          ...conversation,
                          messages: conversation.messages.map((message) => {
                              if (!message.proposals?.some((proposal) => proposal.id === proposalId)) {
                                  return message;
                              }
                              return {
                                  ...message,
                                  proposals: message.proposals.map((proposal) =>
                                      proposal.id === proposalId ? { ...proposal, ...update } : proposal
                                  )
                              };
                          })
                      }
            )
        }));
    }, []);
    const rejectProposal = useCallback(
        (proposalId: string) => updateProposal(proposalId, { rejected: true }),
        [updateProposal]
    );

    return {
        activeConversation,
        chatState,
        createConversation,
        deleteConversation,
        messages,
        renameConversation,
        rejectProposal,
        selectConversation,
        setChatState,
        updateProposal
    };
};
